<template>
  <div v-if="role === 'AGENCY' || role === 'COMPANY'">
    <h3>{{ t('agencySettings') }}</h3>

    <div class="mt-4 grid grid-cols-3 gap-4">
      <div v-if="role === 'AGENCY' && partnerSubType?.type === 'AGENCY_ON_COMMISSION_BASIS'">
        <OverrideDefaultValueField
          v-slot="{ defaultInputBinds }"
          name="agencySettings.commissionPercent"
          type="number"
          :default-value="partnerSubType.agencySettings.commissionPercent"
          :formatted-default-value="`${partnerSubType.agencySettings.commissionPercent}%`"
          :initial-overridden-value="0"
          :label="t('commissionPercent')"
          :disabled="disabled"
          empty-value-to="undefined"
        >
          <Select v-bind="defaultInputBinds" :options="commissionOptions" type="number" />
        </OverrideDefaultValueField>
      </div>

      <div v-if="role === 'COMPANY' && partnerSubType" class="space-y-2">
        <OverrideDefaultValueField
          v-slot="{ defaultInputBinds }"
          name="agencySettings.discountPercent"
          type="number"
          :default-value="partnerSubType.agencySettings.discountPercent"
          :formatted-default-value="`${partnerSubType.agencySettings.discountPercent}%`"
          :initial-overridden-value="0"
          :label="t('discountPercent')"
          :disabled="disabled"
          empty-value-to="undefined"
        >
          <Select v-bind="defaultInputBinds" :options="discountOptions" type="number" />
        </OverrideDefaultValueField>
      </div>

      <div>
        <InputField
          name="agencySettings.invoiceEmail"
          :label="`${t('invoiceEmail')} (${t('optional')})`"
          type="email"
          :disabled="disabled"
        />
      </div>

      <div>
        <InputField
          name="agencySettings.contactPerson"
          :label="`${t('contactPerson')} (${t('optional')})`"
          :disabled="disabled"
        />
      </div>

      <div v-if="role === 'COMPANY'">
        <SelectField
          name="agencySettings.companyInvoicingType"
          :label="t('companyInvoicingType')"
          :options="invoiceTypeOptions"
          empty-value-to="undefined"
        />
      </div>

      <div v-if="role === 'COMPANY' && partnerSubType" class="pt-7">
        <OverrideDefaultValueField
          v-slot="{ defaultInputBinds }"
          name="agencySettings.noDeposit"
          type="boolean"
          :default-value="partnerSubType.agencySettings.noDeposit"
          :formatted-default-value="partnerSubType.agencySettings.noDeposit ? t('yes') : t('no')"
          :initial-overridden-value="false"
          :label="t('noDeposit')"
          :disabled="disabled"
          empty-value-to="undefined"
        >
          <Checkbox v-bind="defaultInputBinds" :label="t('noDeposit')" :disabled="disabled" />
        </OverrideDefaultValueField>
      </div>

      <div v-if="role === 'COMPANY'" class="col-span-3">
        <InputLabel :label="t('allowedEmails')" />
        <p class="mb-2" v-html="t('employeeDomainsExplanation')"></p>
        <MultipleValuesListField
          name="agencySettings.allowedEmployeeDomains"
          :button-text="t('addEmployeeDomain')"
          :field-settings="employeeEmailOrDomainFieldSettings"
          :disabled="disabled"
          show-delete-alert
        />
      </div>
    </div>

    <Divider />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import MultipleValuesListField, {
  type FieldSettings,
} from '@/components/MultipleValuesListField.vue';
import { UserRole } from '@/entities/auth/user-role.enum';
import { CompanyInvoicingType, type PartnerSubType } from '@carvia/ros-client-types';
import { useYieldManagementSettings } from '@/queries/use-yield-management-settings';
import InputLabel from '@/components/InputLabel.vue';
import Divider from '../Divider.vue';
import OverrideDefaultValueField from '../OverrideDefaultValueField.vue';
import Select from '../Select.vue';
import Checkbox from '../Checkbox.vue';

interface Props {
  role?: UserRole;
  partnerSubType?: PartnerSubType;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
});

const { t } = useI18n();

// Get yield management settings for percentage options
const { data: yieldSettings } = useYieldManagementSettings();

// Field settings for combined email or domain list
const employeeEmailOrDomainFieldSettings = computed<FieldSettings[]>(() => [
  {
    name: null,
    placeholder: t('employeeEmailPlaceholder'),
    type: 'text',
  },
]);

const invoiceTypeOptions = computed(() => [
  {
    label: '-',
    value: '',
  },
  ...Object.values(CompanyInvoicingType)
    // TODO: remove this once we have a proper solution for bulk invoicing
    .filter((t) => t !== CompanyInvoicingType.COMPANY_BULK_INVOICE)
    .map((type) => ({
      label: t(`companyInvoicingType.${type}`),
      value: type,
    })),
]);

const commissionOptions = computed(() => [
  {
    label: '-',
    value: 0,
  },
  ...(yieldSettings.value?.commissionPercentageOptions.map((option) => ({
    label: option.label,
    value: option.value,
  })) || []),
]);

const discountOptions = computed(() => [
  {
    label: '-',
    value: 0,
  },
  ...(yieldSettings.value?.discountPercentageOptions.map((option) => ({
    label: option.label,
    value: option.value,
  })) || []),
]);
</script>

<i18n lang="json">
{
  "en": {
    "agencySettings": "Agency / Business Customer Conditions",
    "commissionPercent": "Commission Percent",
    "discountPercent": "Discount Percent",
    "invoiceEmail": "Invoice Email",
    "contactPerson": "Contact Person",
    "contactPersonHint": "Primary contact person for this agent/company",
    "allowedEmployeeDomains": "Allowed Employee Emails or Domains",
    "allowedEmployeeDomainsHint": "Enter either full emails or domains",
    "employeeDomain": "Employee Email or Domain",
    "employeeDomains": "Employee Emails and Domains",
    "addEmployeeDomain": "+ Add Email or Domain",
    "noDeposit": "No Deposit",
    "companyInvoicingType": "Invoicing",
    "employeeEmailPlaceholder": "name{'@'}example.com or example.com",
    "allowedEmails": "Allowed Emails for booking",
    "employeeDomainsExplanation": "Enter either a full email <span class=\"bg-obsidian-200 font-medium px-2 rounded-sm\">max.mustermann{'@'}example.com</span> or only the domain <span class=\"bg-obsidian-200 font-medium px-2 rounded-sm\">example.com</span>.<br>With <span class=\"bg-obsidian-200 font-medium px-2 rounded-sm\">example.com</span>, all tenants with an email ending in <span class=\"bg-obsidian-200 font-medium px-2 rounded-sm\">{'@'}example.com</span> receive the company conditions."
  },
  "de": {
    "agencySettings": "Vermittler- / Businesskundenkonditionen",
    "commissionPercent": "Provisionsprozentsatz",
    "discountPercent": "Rabattprozentsatz",
    "invoiceEmail": "Rechnungs-E-Mail",
    "contactPerson": "Ansprechpartner",
    "contactPersonHint": "Hauptansprechpartner für diesen Agenten/diese Firma",
    "allowedEmployeeDomains": "Erlaubte Mitarbeiter-E-Mails oder Domains",
    "allowedEmployeeDomainsHint": "Gib E-Mail-Adressen oder Domains ein",
    "employeeDomain": "Mitarbeiter-E-Mail oder Domain",
    "employeeDomains": "Mitarbeiter E-Mail Adressen oder Domains",
    "addEmployeeDomain": "+ E-Mail oder Domain hinzufügen",
    "noDeposit": "Keine Kaution",
    "companyInvoicingType": "Abrechnung",
    "employeeEmailPlaceholder": "name{'@'}example.com oder example.com",
    "allowedEmails": "Erlaubte E-Mails zur Buchung",
    "employeeDomainsExplanation": "Gib entweder eine komplette E-Mail-Adresse ein <span class=\"bg-obsidian-200 font-medium px-2 rounded-sm\">max.mustermann{'@'}example.com</span> oder nur die Domain <span class=\"bg-obsidian-200 font-medium px-2 rounded-sm\">example.com</span>.<br>Mit <span class=\"bg-obsidian-200 font-medium px-2 rounded-sm\">example.com</span> erhalten alle Mitarbeiter mit einer <span class=\"bg-obsidian-200 font-medium px-2 rounded-sm\">{'@'}example.com</span> E-Mail-Adresse die Firmenkonditionen."
  },
  "es": {
    "agencySettings": "Condiciones de agencia / cliente empresa",
    "commissionPercent": "Porcentaje de comisión",
    "discountPercent": "Porcentaje de descuento",
    "invoiceEmail": "Correo para facturas",
    "contactPerson": "Persona de contacto",
    "contactPersonHint": "Contacto principal de esta agencia o empresa",
    "allowedEmployeeDomains": "Correos o dominios de empleados permitidos",
    "allowedEmployeeDomainsHint": "Introduzca correos completos o dominios",
    "employeeDomain": "Correo o dominio del empleado",
    "employeeDomains": "Correos y dominios de empleados",
    "addEmployeeDomain": "+ Añadir correo o dominio",
    "noDeposit": "Sin depósito",
    "companyInvoicingType": "Facturación",
    "employeeEmailPlaceholder": "nombre{'@'}ejemplo.com o ejemplo.com",
    "allowedEmails": "Correos permitidos para reservar",
    "employeeDomainsExplanation": "Introduzca un correo completo <span class=\"bg-obsidian-200 font-medium px-2 rounded-sm\">max.mustermann{'@'}example.com</span> o solo el dominio <span class=\"bg-obsidian-200 font-medium px-2 rounded-sm\">example.com</span>.<br>Con <span class=\"bg-obsidian-200 font-medium px-2 rounded-sm\">example.com</span>, todos los arrendatarios con un correo que termine en <span class=\"bg-obsidian-200 font-medium px-2 rounded-sm\">{'@'}example.com</span> reciben las condiciones de empresa."
  }
}
</i18n>
